import { orderBy } from 'lodash-es';
import {
  Case,
  CaseStatus,
  ClinicalData,
  ClinicalItemType,
  OutcomeData,
  TurndownOutcome,
  TurndownRecord,
} from '@/api/apis/case';
import { RouteDeviceTracking } from '@/api/apis/route';
import { formatLactate, formatPFRatio } from '../ClinicalData/sections/utils';
import { getOcsEnd, getOcsStart, getPerfusionTime, getXsTime } from '../ClinicalData/utils';
import {
  formatDate,
  formatDateTimeString,
  getClinicalRecordByType,
  getTxCenterArrivalTime,
  getTxCenterEtaDateTime,
  TIME_WITH_TIMEZONE_FORMAT,
} from '@/utils/helpers';
import { outcomeTypeNames, quoteStatusNames, routeStatusNames } from '@/constants/case/case.constants';
import { atTxCenterStatuses, declineStatuses } from './constants';
import { EtaType } from '@/types';

export const getOutcomeLabel = (oneLungTurndown?: TurndownRecord, outcomeData?: OutcomeData) => {
  if (!oneLungTurndown && outcomeData) return outcomeTypeNames[outcomeData?.outcomeType];
  if (oneLungTurndown?.item?.turndownOutcome === TurndownOutcome.DiscardOrgan) return 'One Lung Discarded';
  if (oneLungTurndown?.item?.turndownOutcome === TurndownOutcome.ShipOrgan) return 'One Lung Shipped';
  return '';
};

export const getOperationTimeline = (status: CaseStatus, clinicalData: ClinicalData) => {
  const inOcsStatuses = [
    CaseStatus.OrganOnOCS,
    CaseStatus.DeployedToTXCenter,
    CaseStatus.AtTXCenter,
    CaseStatus.OrganOffOCS,
  ];

  const getFormattedTime = (showTime: boolean, timeData: { timestamp: string; timezone: string } | null) => {
    if (!showTime || !timeData) {
      return '';
    }

    return formatDate(timeData.timestamp, TIME_WITH_TIMEZONE_FORMAT);
  };

  const xsTime = getXsTime(clinicalData);
  const hasStartTime = inOcsStatuses.includes(status);
  const hasEndTime = status === CaseStatus.OrganOffOCS;
  const onOcsTime = getOcsStart(clinicalData);
  const offOcsTime = getOcsEnd(clinicalData);
  const pfTime = getPerfusionTime(offOcsTime.timestamp, onOcsTime.timestamp);

  return {
    xcTime: getFormattedTime(!!xsTime, xsTime!),
    pfStart: getFormattedTime(hasStartTime, onOcsTime),
    ocsPt: pfTime,
    pfEnd: getFormattedTime(hasEndTime, offOcsTime),
  };
};

export const getBile = (clinicalData: ClinicalData) => {
  const bileRecord = getClinicalRecordByType(clinicalData, ClinicalItemType.Bile);
  const latestBile = orderBy(bileRecord?.items, 'timestamp')?.at(-1)?.yesNo;
  return latestBile;
};

export const getClinicalData = (clinicalData: ClinicalData) => {
  return {
    pfRatio: formatPFRatio(clinicalData),
    lactate: formatLactate(clinicalData, { excludeNullLactate: true }),
    bile: getBile(clinicalData),
  };
};

export const formatCaseData = (caseData: Case) => {
  const { clinicalData } = caseData;
  return {
    baseCaseData: caseData,
    operationTimeline: getOperationTimeline(caseData.status, clinicalData),
    clinicalData: getClinicalData(clinicalData),
  };
};

export const getLogisticsStatuses = (caseData: Case) => {
  const { routePlanningStatus, quote } = caseData;
  const routeStatus = routeStatusNames[routePlanningStatus!];
  const quoteStatus = quote?.status ? quoteStatusNames[quote.status] : '';
  return { routeStatus, quoteStatus };
};

const cardTitleArrivalLabels: Record<string, string> = {
  [EtaType.txCenterSta]: 'TX Center STA:',
  [EtaType.txCenterEta]: 'TX Center ETA:',
};

export const getCardTitleDateTime = (caseData: Case, deviceTracking?: RouteDeviceTracking) => {
  const {
    status,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    txEtaHospitalDate,
    txEtaHospitalTime,
    txCenterDetails,
  } = caseData;
  const atTxCenter = atTxCenterStatuses.includes(status);

  if (!atTxCenter) {
    return {
      label: 'Donor OR:',
      value: formatDateTimeString(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone),
    };
  }

  const txCenterTimezone = txCenterDetails?.location.timezone;

  const arrivalTime = getTxCenterArrivalTime({
    txEtaHospitalDate,
    txEtaHospitalTime,
    txCenterTimezone,
    deviceTracking,
  });

  if (arrivalTime) return { label: cardTitleArrivalLabels[arrivalTime.type], value: arrivalTime.value };

  return { label: 'TX Center STA:', value: '' };
};

export const getCardDateTime = (caseData: Case) => {
  const {
    status,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    txEtaHospitalDate,
    txEtaHospitalTime,
    txCenterDetails,
  } = caseData;
  const atTxCenter = atTxCenterStatuses.includes(status);
  const orDateTime = formatDateTimeString(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const txCenterEta =
    getTxCenterEtaDateTime(txEtaHospitalDate, txEtaHospitalTime, txCenterDetails?.location.timezone) || '';
  return atTxCenter ? txCenterEta : orDateTime;
};

const getOneLungTurndownPrefix = (status: CaseStatus, oneLungTurndown: boolean) => {
  if (!oneLungTurndown) return '';
  if (declineStatuses.includes(status)) return 'Turndown, ';
  return '';
};

export const getOutcomeString = (caseData: Case) => {
  const { clinicalData, outcomeData, status } = caseData;
  const oneLungTurndown = getClinicalRecordByType(clinicalData, ClinicalItemType.OneLungTurndown);
  const prefix = getOneLungTurndownPrefix(status, !!oneLungTurndown);
  const outcome = getOutcomeLabel(oneLungTurndown, outcomeData);
  return `${prefix}${outcome}`;
};
